<template>
  <!-- Click-away listeners -->
  <div v-if="menuOpen" class="fixed inset-0 z-[5]" @click="menuOpen = false" />
  <div v-if="taggedPopoverOpen" class="fixed inset-0 z-[5]" @click="taggedPopoverOpen = false" />

  <!-- ═══ EVENT CARD — distinct layout ═══ -->
  <article
    v-if="isEvent"
    class="rounded-xl border overflow-hidden transition-all shadow-sm hover:shadow-md group"
    :class="[
      'bg-gradient-to-br from-purple-50/80 to-white border-purple-200/80',
      post.isPinned ? 'ring-1 ring-amber-300/60' : '',
      post.isNew ? 'border-l-4 border-l-blue-400' : '',
    ]"
  >
    <!-- Type + author header -->
    <div class="flex items-center justify-between px-5 pt-4 pb-3">
      <div class="flex items-center gap-2 min-w-0">
        <div class="w-7 h-7 rounded-full bg-purple-500 flex items-center justify-center shrink-0">
          <font-awesome-icon :icon="['fas', 'calendar-day']" class="text-xs text-white" />
        </div>
        <span class="text-xs font-semibold text-purple-700 shrink-0">Event</span>
        <span class="text-xs text-gray-500 shrink-0">&middot;</span>
        <span class="text-xs font-semibold text-gray-800 truncate">{{ authorByline }}</span>
        <span
          v-if="post.isPinned"
          class="inline-flex items-center gap-1 text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full shrink-0"
        >
          <font-awesome-icon :icon="['fas', 'thumbtack']" class="text-[9px]" />
          Pinned
        </span>
      </div>
      <div class="flex items-center gap-1.5 shrink-0">
        <span class="text-xs text-gray-400">{{ relativeTime }}</span>
        <div class="relative">
          <button
            class="p-1.5 -mr-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
            :class="menuOpen ? '!opacity-100' : ''"
            @click="menuOpen = !menuOpen"
          >
            <font-awesome-icon :icon="['fas', 'ellipsis']" />
          </button>
          <PostMenu v-if="menuOpen" :is-author="isAuthor" :is-pinned="post.isPinned" @edit="handleEdit" @delete="handleDelete" @pin="handlePin" />
        </div>
      </div>
    </div>

    <!-- Event title -->
    <div class="px-5 pb-3">
      <h3 class="text-lg font-bold text-gray-900">{{ post.title }}</h3>
    </div>

    <!-- Agenda rows -->
    <div class="mx-5 mb-4 rounded-xl bg-white/70 border border-purple-100/60 divide-y divide-purple-100/40">
      <!-- When -->
      <div v-if="post.eventDate" class="flex items-center gap-3.5 px-4 py-3">
        <div class="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center shrink-0">
          <font-awesome-icon :icon="['fas', 'clock']" class="text-sm text-purple-500" />
        </div>
        <div class="min-w-0">
          <p class="text-sm font-semibold text-gray-900">{{ eventDateDisplay }}</p>
          <p class="text-xs text-gray-500">{{ eventTimeDisplay }}</p>
        </div>
      </div>

      <!-- Where -->
      <div v-if="post.eventLocation" class="flex items-center gap-3.5 px-4 py-3">
        <div class="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center shrink-0">
          <font-awesome-icon :icon="['fas', 'location-dot']" class="text-sm text-purple-500" />
        </div>
        <p class="text-sm font-medium text-gray-800">{{ post.eventLocation }}</p>
      </div>

      <!-- Who (audience) -->
      <div class="flex items-center gap-3.5 px-4 py-3">
        <div class="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center shrink-0">
          <font-awesome-icon :icon="['fas', 'users']" class="text-sm text-purple-500" />
        </div>
        <p class="text-sm font-medium text-gray-800">{{ eventAudience }}</p>
      </div>
    </div>

    <!-- Description -->
    <div v-if="post.body" class="px-5 pb-2">
      <p class="text-sm text-gray-600 whitespace-pre-line leading-relaxed">{{ post.body }}</p>
    </div>

    <!-- Photo attachments -->
    <div v-if="eventAttachments.length" class="px-5 pb-4">
      <div class="flex gap-2 overflow-x-auto">
        <button
          v-for="(att, i) in eventAttachments.slice(0, 4)"
          :key="att.id"
          class="relative shrink-0 w-20 h-20 rounded-lg overflow-hidden border border-gray-200 hover:opacity-90 transition-opacity"
          @click="openLightbox(i)"
        >
          <img :src="att.url" :alt="att.fileName" class="w-full h-full object-cover" />
          <span
            v-if="i === 3 && eventAttachments.length > 4"
            class="absolute inset-0 bg-black/50 flex items-center justify-center text-white text-sm font-bold"
          >
            +{{ eventAttachments.length - 4 }}
          </span>
        </button>
      </div>
    </div>

  </article>

  <!-- ═══ STANDARD POST CARD ═══ -->
  <article
    v-else
    class="rounded-xl border p-5 transition-all shadow-sm hover:shadow-md"
    :class="cardClasses"
  >
    <!-- Typed posts: circle icon + type + author inline -->
    <div v-if="isTyped" class="flex items-center justify-between mb-3">
      <div class="flex items-center gap-2 min-w-0">
        <div
          class="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
          :class="typeStyle.circleClass"
        >
          <font-awesome-icon :icon="typeStyle.icon" class="text-xs text-white" />
        </div>
        <span class="text-xs font-semibold shrink-0" :class="typeStyle.labelColor">{{ typeStyle.label }}</span>
        <span class="text-xs text-gray-500 shrink-0">&middot;</span>
        <span class="text-xs font-semibold text-gray-800 truncate">{{ authorByline }}</span>
        <span
          v-if="post.isPinned"
          class="inline-flex items-center gap-1 text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full shrink-0"
        >
          <font-awesome-icon :icon="['fas', 'thumbtack']" class="text-[9px]" />
          Pinned
        </span>
      </div>
      <div class="flex items-center gap-1.5 shrink-0">
        <span class="text-xs text-gray-400">{{ relativeTime }}</span>
        <div class="relative">
          <button
            class="p-1.5 -mr-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
            :class="menuOpen ? '!opacity-100' : ''"
            @click="menuOpen = !menuOpen"
          >
            <font-awesome-icon :icon="['fas', 'ellipsis']" />
          </button>
          <PostMenu v-if="menuOpen" :is-author="isAuthor" :is-pinned="post.isPinned" @edit="handleEdit" @delete="handleDelete" @pin="handlePin" />
        </div>
      </div>
    </div>

    <!-- Plain (MESSAGE) posts: author row with inline meta -->
    <div v-else class="flex items-center gap-2.5 mb-3">
      <component
        :is="showSpaceBadge && post.spaceId ? RouterLink : 'div'"
        v-bind="showSpaceBadge && post.spaceId ? { to: `/connect/spaces/${post.spaceId}` } : {}"
        class="w-10 h-10 flex items-center justify-center shrink-0 text-xs font-bold text-white shadow-sm"
        :class="[
          isTeacherPost ? 'rounded-full' : 'rounded-xl',
          showSpaceBadge && post.spaceId ? 'hover:opacity-90 transition-opacity' : '',
        ]"
        :style="{ backgroundColor: avatarColor }"
      >
        {{ avatarInitials }}
      </component>

      <div class="min-w-0 flex-1">
        <p class="text-sm font-semibold text-gray-900 truncate">{{ displayName }}</p>
        <div class="flex items-center gap-1.5">
          <template v-if="bylineContext">
            <RouterLink
              v-if="showSpaceBadge && post.spaceId"
              :to="`/connect/spaces/${post.spaceId}`"
              class="text-xs text-gray-500 hover:text-gray-700 transition-colors truncate"
            >
              {{ bylineContext }}
            </RouterLink>
            <span v-else class="text-xs text-gray-500 truncate">{{ bylineContext }}</span>
          </template>
          <font-awesome-icon
            v-if="post.isPinned"
            :icon="['fas', 'thumbtack']"
            class="text-amber-500 text-[10px] shrink-0"
          />
        </div>
      </div>

      <div class="flex items-center gap-1.5 shrink-0">
        <span class="text-xs text-gray-400">{{ relativeTime }}</span>
        <div class="relative">
          <button
            class="p-1.5 -mr-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
            :class="menuOpen ? '!opacity-100' : ''"
            @click="menuOpen = !menuOpen"
          >
            <font-awesome-icon :icon="['fas', 'ellipsis']" />
          </button>
          <PostMenu v-if="menuOpen" :is-author="isAuthor" :is-pinned="post.isPinned" @edit="handleEdit" @delete="handleDelete" @pin="handlePin" />
        </div>
      </div>
    </div>

    <!-- Content -->
    <div>
      <h3
        v-if="post.title"
        class="text-gray-900 mb-1.5"
        :class="post.type === 'ANNOUNCEMENT' ? 'text-base font-bold' : 'text-sm font-semibold'"
      >
        {{ post.title }}
      </h3>
      <p v-if="post.body" class="text-sm text-gray-600 whitespace-pre-line leading-relaxed">{{ post.body }}</p>
    </div>

    <!-- Assignment details box -->
    <div
      v-if="post.type === 'ASSIGNMENT' && post.assignment"
      class="mt-3 rounded-lg bg-cyan-50/60 border border-cyan-200/60 p-3"
    >
      <div class="flex items-start gap-3">
        <div class="w-9 h-9 rounded-lg bg-cyan-100 flex items-center justify-center shrink-0">
          <font-awesome-icon :icon="['fas', 'file-lines']" class="text-sm text-cyan-600" />
        </div>
        <div class="flex-1 min-w-0">
          <p class="text-sm font-semibold text-gray-900">{{ post.assignment.name }}</p>
          <p v-if="post.assignment.categoryName" class="text-xs text-cyan-600 mt-0.5">
            {{ post.assignment.categoryName }}
          </p>
          <div class="flex items-center justify-between mt-2">
            <p v-if="post.assignment.dateDue" class="text-xs text-gray-500">
              Due: {{ formatAssignmentDate(post.assignment.dateDue) }}
            </p>
            <div v-else />
            <div v-if="isTeacherView && post.assignment.totalStudents > 0" class="flex items-center gap-1.5">
              <svg class="w-4 h-4 -rotate-90" viewBox="0 0 36 36">
                <circle cx="18" cy="18" r="14" fill="none" stroke="currentColor" stroke-width="4" class="text-gray-200" />
                <circle
                  cx="18" cy="18" r="14" fill="none" stroke="currentColor" stroke-width="4"
                  class="text-cyan-600"
                  :stroke-dasharray="`${(post.assignment.submissionCount / post.assignment.totalStudents) * 88} 88`"
                  stroke-linecap="round"
                />
              </svg>
              <span class="text-xs font-medium text-cyan-600">
                {{ post.assignment.submissionCount }}/{{ post.assignment.totalStudents }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Photo attachments -->
    <div v-if="attachments.length" class="mt-3">
      <div class="flex gap-2 overflow-x-auto">
        <button
          v-for="(att, i) in attachments.slice(0, 4)"
          :key="att.id"
          class="relative shrink-0 w-24 h-24 rounded-lg overflow-hidden border border-gray-200 hover:opacity-90 transition-opacity"
          @click="openLightbox(i)"
        >
          <img :src="att.url" :alt="att.fileName" class="w-full h-full object-cover" />
          <span
            v-if="i === 3 && attachments.length > 4"
            class="absolute inset-0 bg-black/50 flex items-center justify-center text-white text-sm font-bold"
          >
            +{{ attachments.length - 4 }}
          </span>
        </button>
      </div>
    </div>

    <!-- Footer: tagged people -->
    <div
      v-if="hasTaggedPeople"
      class="mt-3 pt-3 border-t flex items-center gap-2 flex-wrap"
      :class="isTyped ? 'border-gray-200/60' : 'border-gray-100'"
    >
      <div class="relative" :class="taggedPopoverOpen ? 'z-20' : ''">
        <button
          class="flex items-center gap-1.5 text-xs hover:bg-gray-50 rounded-lg px-1.5 py-1 -mx-1.5 -my-1 transition-colors"
          @click="taggedPopoverOpen = !taggedPopoverOpen"
        >
          <font-awesome-icon
            :icon="post.visibility === 'TAGGED' ? ['fas', 'eye-slash'] : ['fas', 'globe']"
            class="text-[10px]"
            :class="post.visibility === 'TAGGED' ? 'text-violet-400' : 'text-gray-400'"
          />
          <span class="text-gray-500">
            <span class="font-medium" :class="post.visibility === 'TAGGED' ? 'text-violet-600' : 'text-gray-700'">
              {{ taggedNamesDisplay }}
            </span>
          </span>
        </button>
        <!-- Tagged names popover -->
        <div
          v-if="taggedPopoverOpen"
          class="absolute bottom-full left-0 mb-1.5 w-56 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-20"
        >
          <p class="px-3 pb-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Tagged students</p>
          <div v-if="taggedNames.length" class="max-h-40 overflow-y-auto">
            <p
              v-for="name in taggedNames"
              :key="name"
              class="px-3 py-1.5 text-sm text-gray-700"
            >
              {{ name }}
            </p>
          </div>
          <div v-else class="px-3 py-1.5">
            <p class="text-sm text-gray-500">{{ (post.taggedStudentIds?.length ?? 0) }} {{ (post.taggedStudentIds?.length ?? 0) === 1 ? 'student' : 'students' }}</p>
            <RouterLink
              v-if="post.spaceId"
              :to="`/connect/spaces/${post.spaceId}`"
              class="text-xs text-indigo-600 hover:text-indigo-700 font-medium mt-1 inline-block"
            >
              View in space &rarr;
            </RouterLink>
          </div>
        </div>
      </div>
    </div>
  </article>

  <!-- Photo lightbox -->
  <Teleport to="body">
    <div
      v-if="lightboxIndex !== null"
      class="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
      @click.self="lightboxIndex = null"
    >
      <button
        class="absolute top-4 right-4 text-white/70 hover:text-white text-2xl z-10"
        @click="lightboxIndex = null"
      >
        <font-awesome-icon :icon="['fas', 'xmark']" />
      </button>
      <button
        v-if="lightboxIndex > 0"
        class="absolute left-4 text-white/70 hover:text-white text-2xl z-10"
        @click="lightboxIndex!--"
      >
        <font-awesome-icon :icon="['fas', 'chevron-left']" />
      </button>
      <button
        v-if="lightboxIndex < allAttachments.length - 1"
        class="absolute right-14 text-white/70 hover:text-white text-2xl z-10"
        @click="lightboxIndex!++"
      >
        <font-awesome-icon :icon="['fas', 'chevron-right']" />
      </button>
      <img
        :src="allAttachments[lightboxIndex]?.url"
        :alt="allAttachments[lightboxIndex]?.fileName"
        class="max-w-[90vw] max-h-[90vh] object-contain rounded-lg"
      />
      <div class="absolute bottom-4 text-white/60 text-sm">
        {{ (lightboxIndex ?? 0) + 1 }} / {{ allAttachments.length }}
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, defineComponent, h } from 'vue'
import { RouterLink } from 'vue-router'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import type { SpacePost } from '@/api'
import { resolveFileUrl } from '@/api'

const PostMenu = defineComponent({
  props: { isAuthor: Boolean, isPinned: Boolean },
  emits: ['edit', 'delete', 'pin'],
  setup(props, { emit }) {
    return () => h('div', {
      class: 'absolute right-0 top-full mt-1 w-36 bg-white rounded-xl shadow-lg border border-gray-200 py-1 z-10',
    }, [
      props.isAuthor ? h('button', {
        class: 'w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2',
        onClick: () => emit('edit'),
      }, [h(FontAwesomeIcon, { icon: ['fas', 'pen'], class: 'text-xs text-gray-400' }), ' Edit']) : null,
      h('button', {
        class: 'w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2',
        onClick: () => emit('pin'),
      }, [h(FontAwesomeIcon, { icon: ['fas', 'thumbtack'], class: 'text-xs text-gray-400' }), props.isPinned ? ' Unpin' : ' Pin']),
      props.isAuthor ? h('button', {
        class: 'w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2',
        onClick: () => emit('delete'),
      }, [h(FontAwesomeIcon, { icon: ['fas', 'trash'], class: 'text-xs text-red-400' }), ' Delete']) : null,
    ])
  },
})

const props = defineProps<{
  post: SpacePost
  spaceName?: string
  showSpaceBadge?: boolean
  currentUserId?: string
  studentNameMap?: Record<string, string>
  isTeacher?: boolean
}>()

const emit = defineEmits<{
  edit: [post: SpacePost]
  delete: [post: SpacePost]
  pin: [post: SpacePost]
}>()

const menuOpen = ref(false)
const taggedPopoverOpen = ref(false)
const lightboxIndex = ref<number | null>(null)

const allAttachments = computed(() =>
  (props.post.attachments ?? [])
    .filter(a => a.url)
    .map(a => ({ ...a, url: resolveFileUrl(a.url!) })),
)
const attachments = allAttachments // alias for standard card
const eventAttachments = allAttachments // alias for event card

function openLightbox(index: number) {
  lightboxIndex.value = index
}

const isEvent = computed(() => props.post.type === 'EVENT')
const isTyped = computed(() => props.post.type !== 'MESSAGE' && !isEvent.value)
const hasTaggedPeople = computed(() => (props.post.taggedStudentIds?.length ?? 0) > 0)

const typeStyles: Record<string, {
  icon: string[]; label: string; circleClass: string; labelColor: string; badgeClass: string; cardBg: string; cardBorder: string
}> = {
  ANNOUNCEMENT: {
    icon: ['fas', 'bullhorn'], label: 'Announcement',
    circleClass: 'bg-blue-500',
    labelColor: 'text-blue-700',
    badgeClass: 'bg-blue-100 text-blue-700',
    cardBg: 'bg-gradient-to-br from-blue-50/60 to-white', cardBorder: 'border-blue-200/80',
  },
  REMINDER: {
    icon: ['fas', 'bell'], label: 'Reminder',
    circleClass: 'bg-amber-500',
    labelColor: 'text-amber-700',
    badgeClass: 'bg-amber-100 text-amber-700',
    cardBg: 'bg-gradient-to-br from-amber-50/60 to-white', cardBorder: 'border-amber-200/80',
  },
  MOMENT: {
    icon: ['fas', 'camera'], label: 'Moment',
    circleClass: 'bg-emerald-500',
    labelColor: 'text-emerald-700',
    badgeClass: 'bg-emerald-100 text-emerald-700',
    cardBg: 'bg-gradient-to-br from-emerald-50/50 to-white', cardBorder: 'border-emerald-200/80',
  },
  ASSIGNMENT: {
    icon: ['fas', 'file-lines'], label: 'Assignment',
    circleClass: 'bg-cyan-600',
    labelColor: 'text-cyan-700',
    badgeClass: 'bg-cyan-100 text-cyan-700',
    cardBg: 'bg-gradient-to-br from-cyan-50/60 to-white', cardBorder: 'border-cyan-200/80',
  },
}

const typeStyle = computed(() => typeStyles[props.post.type] ?? { icon: [], label: '', circleClass: '', labelColor: '', badgeClass: '', cardBg: '', cardBorder: '' })

const cardClasses = computed(() => {
  const isNew = props.post.isNew ? 'border-l-4 border-l-blue-400' : ''
  if (isTyped.value) {
    const ts = typeStyle.value
    const pinned = props.post.isPinned ? 'ring-1 ring-amber-300/60' : ''
    return `${ts.cardBg} ${ts.cardBorder} ${pinned} ${isNew} group`
  }
  const pinned = props.post.isPinned ? 'ring-1 ring-amber-300/60' : ''
  return `bg-white border-gray-200 ${pinned} ${isNew} group`
})

const taggedNames = computed(() => {
  const ids = props.post.taggedStudentIds ?? []
  const map = props.studentNameMap ?? {}
  return ids.map(id => map[id]).filter(Boolean) as string[]
})

const taggedNamesDisplay = computed(() => {
  const ids = props.post.taggedStudentIds ?? []
  if (!ids.length) return ''
  const names = taggedNames.value
  if (!names.length) return `${ids.length} ${ids.length === 1 ? 'person' : 'people'} tagged`
  if (names.length <= 2) return `with ${names.join(' and ')}`
  return `with ${names[0]}, ${names[1]}, and ${names.length - 2} ${names.length - 2 === 1 ? 'other' : 'others'}`
})

const spaceColors = [
  '#3B82F6', '#8B5CF6', '#EC4899', '#F59E0B', '#10B981',
  '#6366F1', '#EF4444', '#14B8A6', '#F97316', '#06B6D4',
]

function smartInitials(name: string): string {
  const words = name.trim().split(/\s+/)
  const numMatch = name.match(/\d+/)
  if (numMatch) {
    const nonNumWord = words.find(w => !/^\d+$/.test(w))
    if (nonNumWord) return numMatch[0] + nonNumWord[0]!.toUpperCase()
    return numMatch[0]!.substring(0, 2)
  }
  if (words.length >= 2) return (words[0]![0]! + words[1]![0]!).toUpperCase()
  return name.substring(0, 2).toUpperCase()
}

const isTeacherPost = computed(() => props.post.showAuthor)

// Byline: "Author · Context" when showAuthor, or just "Context" when anonymous
const displayName = computed(() => {
  if (isTeacherPost.value && props.post.authorName) return props.post.authorName
  return props.post.contextName || props.spaceName || 'Space'
})

// Combined author byline: "Mr Jones, 7 Blue" or "Huntcliff School"
const authorByline = computed(() => {
  if (isTeacherPost.value && props.post.authorName) {
    const context = props.post.contextName || props.spaceName
    return context ? `${props.post.authorName}, ${context}` : props.post.authorName
  }
  return props.post.contextName || props.spaceName || 'Space'
})

// Context part after the middot (only for teacher posts)
const bylineContext = computed(() => {
  if (!isTeacherPost.value) return null
  return props.post.contextName || props.spaceName || null
})

const avatarInitials = computed(() => {
  if (isTeacherPost.value && props.post.authorName) return smartInitials(props.post.authorName)
  return smartInitials(props.post.contextName || props.spaceName || '?')
})

const avatarColor = computed(() => {
  const name = isTeacherPost.value && props.post.authorName
    ? props.post.authorName
    : (props.post.contextName || props.spaceName || '')
  let hash = 0
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash)
  return spaceColors[Math.abs(hash) % spaceColors.length]!
})


const isTeacherView = computed(() => props.isTeacher ?? !!props.currentUserId)
const isAuthor = computed(() => props.currentUserId === props.post.createdBy)

const relativeTime = computed(() => {
  const now = new Date()
  const date = new Date(props.post.createdAt)
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  const diffHours = Math.floor(diffMins / 60)
  if (diffHours < 24) return `${diffHours}h ago`
  const diffDays = Math.floor(diffHours / 24)
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
})

const eventDateDisplay = computed(() => {
  if (!props.post.eventDate) return ''
  const d = new Date(props.post.eventDate)
  return d.toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
})

const eventTimeDisplay = computed(() => {
  if (!props.post.eventDate) return ''
  if (props.post.eventAllDay) return 'All day'
  const d = new Date(props.post.eventDate)
  const start = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
  if (props.post.eventEndDate) {
    const end = new Date(props.post.eventEndDate).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
    return `${start} – ${end}`
  }
  return start
})

const eventAudience = computed(() => {
  return props.post.contextName || props.spaceName || 'School'
})

function formatAssignmentDate(dateStr: string): string {
  try {
    const d = new Date(dateStr)
    return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })
  } catch {
    return dateStr
  }
}

function handleEdit() { menuOpen.value = false; emit('edit', props.post) }
function handleDelete() { menuOpen.value = false; emit('delete', props.post) }
function handlePin() { menuOpen.value = false; emit('pin', props.post) }
</script>
